import { useCallback, useEffect, useState, type KeyboardEvent } from "react"

export type FaceTabItem = { id: string; label: string }

/**
 * Controlled or uncontrolled tab index for the face region strip.
 */
export function useFaceTabsViewModel(options: {
  tabs: FaceTabItem[]
  activeIndex?: number
  onActiveIndexChange?: (index: number) => void
}) {
  const { tabs, activeIndex, onActiveIndexChange } = options
  const isControlled = typeof activeIndex === "number"
  const [uncontrolledIndex, setUncontrolledIndex] = useState(0)

  useEffect(() => {
    if (isControlled) {
      return
    }
    if (uncontrolledIndex >= tabs.length && tabs.length > 0) {
      setUncontrolledIndex(0)
    }
  }, [tabs.length, uncontrolledIndex, isControlled])

  const currentIndex = isControlled
    ? Math.min(Math.max(activeIndex as number, 0), Math.max(0, tabs.length - 1))
    : uncontrolledIndex

  const setIndex = useCallback(
    (index: number) => {
      const clamped = Math.min(Math.max(index, 0), Math.max(0, tabs.length - 1))
      if (isControlled) {
        onActiveIndexChange?.(clamped)
      } else {
        setUncontrolledIndex(clamped)
        onActiveIndexChange?.(clamped)
      }
    },
    [isControlled, tabs.length, onActiveIndexChange],
  )

  const handleClick = useCallback((index: number) => setIndex(index), [setIndex])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent, index: number) => {
      if (tabs.length === 0) {
        return
      }
      if (e.key === "ArrowRight") {
        e.preventDefault()
        setIndex((index + 1) % tabs.length)
      } else if (e.key === "ArrowLeft") {
        e.preventDefault()
        setIndex((index - 1 + tabs.length) % tabs.length)
      } else if (e.key === "Home") {
        e.preventDefault()
        setIndex(0)
      } else if (e.key === "End") {
        e.preventDefault()
        setIndex(tabs.length - 1)
      }
    },
    [tabs.length, setIndex],
  )

  const getTabId = useCallback((index: number) => `uv-face-tab-${index}`, [])
  const getPanelId = useCallback((index: number) => `uv-face-panel-${index}`, [])

  return {
    currentIndex,
    handleClick,
    handleKeyDown,
    getTabId,
    getPanelId,
  }
}
